// altitudeSource — best-available aircraft altitude for safety-critical
// consumers (terrain advisor, AGL display, NEMSIS export).
//
// Why this exists:
//   The terrain advisor's first cut consumed raw GPS altitude
//   (`pos.coords.altitude`) directly. On most consumer devices that
//   number is noisy at ±200–500 ft and can dip transiently even when
//   the aircraft is in level flight, causing the TAWS to false-alert
//   "below 200 ft AGL" while cruising 1500 ft above the ridgeline.
//
//   The right answer in aviation is barometric altitude:
//     - From an ADS-B-In receiver (Stratus, GDL-90, Sentry, etc.)
//     - From the iPad's barometric pressure sensor (CMAltimeter on iOS
//       Pro models — exposed via Capacitor / native bridge)
//     - From a panel-mounted altimeter via Bluetooth (future)
//
//   This module is the ONE place that decides which source wins. It
//   exposes:
//     - `setBaroAltFt(ft, timestampMs)` — push from any baro source.
//     - `useSmoothedAltFt()` — React hook returning the best
//        altitude in feet, or null when none is available.
//
// Source priority (highest wins, fall-through to next on stale/missing):
//   1. Baro altitude pushed within the last 10 s.
//   2. Smoothed GPS altitude (10-sample moving average; min 3 samples).
//   3. Raw GPS altitude (single sample, for early-fix display).
//   4. null.
//
// The smoother uses a fixed-size ring buffer of {altFt, t} samples
// and exposes `pruneStaleSamples` so consumers can adjust the window.

import { useEffect, useState } from 'react';
import { useGpsStore } from '../stores/gpsStore';

const M_TO_FT = 3.28084;

// ── Baro source registry ─────────────────────────────────────────────────

interface BaroSample {
  altFt: number;
  t: number;          // wall-clock ms
  /** Source label (for debugging / telemetry). */
  source: string;
}

let latestBaro: BaroSample | null = null;
/** Baro samples older than this are ignored. */
const BARO_TTL_MS = 10_000;

/**
 * Push a barometric altitude sample. Call from:
 *   - ADS-B traffic ingest (GDL-90 ownship alt is baro)
 *   - iOS CMAltimeter bridge
 *   - Future panel-altimeter Bluetooth
 *
 * `timestampMs` defaults to Date.now(); pass the source's own
 * timestamp when available (the ADS-B feed timestamps every packet).
 */
export function setBaroAltFt(altFt: number, source: string, timestampMs?: number): void {
  if (!Number.isFinite(altFt)) return;
  latestBaro = { altFt, t: timestampMs ?? Date.now(), source };
}

/** For tests + dev console. */
export function clearBaroAlt(): void {
  latestBaro = null;
}

/** Most-recent baro altitude in feet, or null if none / stale. */
export function getBaroAltFt(now: number = Date.now()): { altFt: number; source: string } | null {
  if (!latestBaro) return null;
  if (now - latestBaro.t > BARO_TTL_MS) return null;
  return { altFt: latestBaro.altFt, source: latestBaro.source };
}

// ── GPS smoothing ────────────────────────────────────────────────────────

interface GpsAltSample {
  altFt: number;
  t: number;
}

/**
 * Smooth a sequence of GPS altitude samples into a single value.
 *
 * Algorithm: simple moving average of the last N samples within the
 * window (windowMs). If fewer than `minSamples` are present, return
 * null — the smoother refuses to commit to a value until it has
 * enough signal. This prevents the first fix from immediately
 * triggering a low-AGL alert if the initial sample reads abnormally
 * low.
 *
 * Returned value is in FEET.
 */
export function smoothGpsAlt(
  samples: GpsAltSample[],
  opts: { now?: number; windowMs?: number; minSamples?: number } = {},
): number | null {
  const now = opts.now ?? Date.now();
  const windowMs = opts.windowMs ?? 10_000;
  const minSamples = opts.minSamples ?? 3;
  const cutoff = now - windowMs;
  const fresh = samples.filter((s) => s.t >= cutoff && Number.isFinite(s.altFt));
  if (fresh.length < minSamples) return null;
  const sum = fresh.reduce((acc, s) => acc + s.altFt, 0);
  return sum / fresh.length;
}

/** Drop samples older than `windowMs` from the array. */
export function pruneStaleSamples(
  samples: GpsAltSample[],
  windowMs: number = 10_000,
  now: number = Date.now(),
): GpsAltSample[] {
  const cutoff = now - windowMs;
  return samples.filter((s) => s.t >= cutoff);
}

// ── React hook ───────────────────────────────────────────────────────────

export interface AltitudeReading {
  /** Best altitude in feet, or null if no source is available. */
  altFt: number | null;
  /** Which source produced this number — for instrument badge / debug. */
  source: 'baro' | 'gps-smoothed' | 'gps-raw' | 'none';
  /** When true, this reading is suitable for safety-critical use
   *  (terrain alerts, AGL display). False during first-fix warm-up. */
  trustworthy: boolean;
}

/**
 * Returns the best-available altitude, with source attribution.
 *
 * Implementation detail: we keep the GPS ring buffer at module scope
 * (not state) so re-renders don't churn it. The component subscribed
 * to the GPS store will re-render on every fix, and we update the
 * exposed reading from inside that render path.
 *
 * `bufferRef` is reset (cleared) when the component unmounts so a
 * fresh mount starts with a clean buffer — important so e.g. a panel
 * re-open after 30 min of standby doesn't reuse stale samples.
 */
let gpsBuffer: GpsAltSample[] = [];

export function useSmoothedAltFt(opts: { windowMs?: number; minSamples?: number } = {}): AltitudeReading {
  const fix = useGpsStore((s) => s.fix);
  const [reading, setReading] = useState<AltitudeReading>({ altFt: null, source: 'none', trustworthy: false });

  // Reset buffer on mount/unmount so re-opens of a consumer hook
  // don't reuse samples from minutes ago.
  useEffect(() => {
    return () => {
      gpsBuffer = [];
    };
  }, []);

  useEffect(() => {
    const now = Date.now();
    // 1. Baro wins if fresh.
    const baro = getBaroAltFt(now);
    if (baro) {
      setReading({ altFt: baro.altFt, source: 'baro', trustworthy: true });
      return;
    }
    // 2. Smoothed GPS.
    if (fix && fix.altMeters != null && Number.isFinite(fix.altMeters)) {
      gpsBuffer.push({ altFt: fix.altMeters * M_TO_FT, t: fix.timestamp });
      gpsBuffer = pruneStaleSamples(gpsBuffer, opts.windowMs ?? 10_000, now);
      const smoothed = smoothGpsAlt(gpsBuffer, { now, windowMs: opts.windowMs, minSamples: opts.minSamples });
      if (smoothed != null) {
        setReading({ altFt: smoothed, source: 'gps-smoothed', trustworthy: true });
        return;
      }
      // 3. Not enough samples yet — show raw, but mark untrustworthy.
      setReading({ altFt: fix.altMeters * M_TO_FT, source: 'gps-raw', trustworthy: false });
      return;
    }
    setReading({ altFt: null, source: 'none', trustworthy: false });
  }, [fix?.altMeters, fix?.timestamp, opts.windowMs, opts.minSamples, fix]);

  return reading;
}

// ── Internal exports for tests ───────────────────────────────────────────
// Resetting the module-level buffer between tests.
export function __resetGpsBufferForTests(): void {
  gpsBuffer = [];
}
